import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDrag, useDrop } from "react-dnd";
import {
  inclusionMaster,
  inclusionData,
} from "../packageService";
import {
  pbAddBtnMt,
  pbDeleteBtn,
  pbListFieldInput,
} from "../packageBuilderUi";

// Sortable Item Component
const SortableItem = ({
  item,
  index,
  field,
  placeholder,
  moveItem,
  handleArrayItemChange,
  removeArrayItem,
}) => {
  const ref = React.useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: "item",
    item: { index, field },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "item",
    hover(draggedItem, monitor) {
      if (!ref.current) return;

      // Don't replace items with themselves
      if (draggedItem.index === index || draggedItem.field !== field) return;

      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      // Time to actually perform the action
      moveItem(field, dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      draggedItem.index = hoverIndex;
    },
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`flex gap-2 mb-1 ${isDragging ? "opacity-50" : ""}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <input
        type="text"
        value={item}
        onChange={(e) => handleArrayItemChange(field, index, e.target.value)}
        className={pbListFieldInput}
        placeholder={placeholder}
      />
      <button
        onClick={() => removeArrayItem(field, index)}
        className={pbDeleteBtn}
      >
        <i className="fi fi-rr-trash flex items-center justify-center"></i>
      </button>
      <button
        className={`
          border border-gray-200 
          hover:border-gray-400 
          hover:bg-gray-100 
          bg-gray-50 
          p-1.5 
          rounded-full 
          transition-all 
          duration-200 
          cursor-move 
          group 
          ${isDragging ? "border-gray-400 bg-gray-100 shadow-sm" : ""}
        `}
        title="Drag to reorder"
      >
        <i
          className={`
          fi fi-rr-grip-dots
          flex items-center 
          justify-center
          text-gray-400 
          group-hover:text-gray-600
          transition-colors
          duration-200
          ${isDragging ? "text-gray-600" : ""}
        `}
        ></i>
      </button>
    </div>
  );
};

const InclusionHighlightIcon = ({ item }) => {
  if (item.icon_url) {
    return (
      <img
        src={item.icon_url}
        alt={item.name}
        className="w-5 h-5 object-contain"
      />
    );
  }

  return (
    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
      {item.name?.charAt(0)?.toUpperCase() || "?"}
    </span>
  );
};

const Inclusion = ({ setInclusionData, packageid, onUserEdit }) => {
  // State for form data
  const [formData, setFormData] = useState({
    inclusion_highlights: [],
    exclusions: [""],
    things_to_carry: [""],
    detailed_inclusions: [""],
    package_id: packageid,
  });

  const [highlightError, setHighlightError] = useState(null);

  const { data: inclusionList = { data: [] } } = useQuery({
    queryKey: ["inclusion-master-list"],
    queryFn: inclusionMaster,
  });

  const masterHighlights = inclusionList?.data ?? [];
  const hasMasterHighlights = masterHighlights.length > 0;

  // Fetch package inclusion data if packageid exists
  const { data: packageInclusion } = useQuery({
    queryKey: ["packageInclusion", packageid],
    queryFn: () => inclusionData(packageid),
    enabled: !!packageid,
  });

  // Update form data when package inclusion data is fetched
  useEffect(() => {
    if (packageInclusion?.data) {
      setFormData((prev) => ({
        ...prev,
        package_id: packageid,
        inclusion_highlights:
          packageInclusion.data.package_inclusion_highlights?.map(
            (item) => item.inclusion_id
          ) || [],
        exclusions: packageInclusion.data.package_exclusions?.map(
          (item) => item.description
        ) || [""],
        things_to_carry: packageInclusion.data.package_things_to_carry?.map(
          (item) => item.description
        ) || [""],
        detailed_inclusions:
          packageInclusion.data.package_detailed_inclusions?.map(
            (item) => item.description
          ) || [""],
      }));
    }
  }, [packageInclusion, packageid]);

  // Update parent component data
  useEffect(() => {
    if (setInclusionData) {
      const formattedData = {
        ...formData,
        // Filter out empty strings from arrays
        exclusions: formData.exclusions.filter((item) => item.trim() !== ""),
        things_to_carry: formData.things_to_carry.filter(
          (item) => item.trim() !== ""
        ),
        detailed_inclusions: formData.detailed_inclusions.filter(
          (item) => item.trim() !== ""
        ),
        package_id: packageid,
        masterHighlightsAvailable: hasMasterHighlights,
        isUpdate: !!(
          packageInclusion?.data?.package_exclusions?.length ||
          packageInclusion?.data?.package_things_to_carry?.length ||
          packageInclusion?.data?.package_detailed_inclusions?.length ||
          packageInclusion?.data?.package_inclusion_highlights?.length
        ),
        current_stage: 5,
        is_draft: 0,
        onError: (error) => {
          if (error.response?.status === 422) {
            try {
              const errorMessage = error.response.data.message;
              const parsedErrors = JSON.parse(errorMessage);
              if (parsedErrors.inclusion_highlights) {
                setHighlightError("Please select at least one inclusion highlight");
              }
            } catch (e) {
              console.error("Error parsing validation errors:", e);
            }
          }
        },
      };

      setInclusionData(formattedData);
    }
  }, [formData, setInclusionData, packageid, packageInclusion, hasMasterHighlights]);

  // Move item function for drag and drop
  const moveItem = (field, dragIndex, hoverIndex) => {
    const draggedItem = formData[field][dragIndex];
    const updatedItems = [...formData[field]];
    updatedItems.splice(dragIndex, 1);
    updatedItems.splice(hoverIndex, 0, draggedItem);

    setFormData((prev) => ({
      ...prev,
      [field]: updatedItems,
    }));
  };

  // Event handlers
  const handleHighlightToggle = (id) => {
    onUserEdit?.();
    setHighlightError(null);
    setFormData((prev) => ({
      ...prev,
      inclusion_highlights: prev.inclusion_highlights.includes(id)
        ? prev.inclusion_highlights.filter((item) => item !== id)
        : [...prev.inclusion_highlights, id],
    }));
  };

  const handleArrayItemChange = (field, index, value) => {
    onUserEdit?.();
    setFormData((prev) => {
      const newArray = [...prev[field]];
      newArray[index] = value;
      return { ...prev, [field]: newArray };
    });
  };

  const addArrayItem = (field) => {
    onUserEdit?.();
    setFormData((prev) => ({
      ...prev,
      [field]: [...(prev[field] || []), ""],
    }));
  };

  const removeArrayItem = (field, index) => {
    onUserEdit?.();
    setFormData((prev) => {
      // Ensure at least one empty field remains
      if (prev[field].length <= 1) {
        return { ...prev, [field]: [""] };
      }
      return {
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index),
      };
    });
  };

  // Ensure arrays have at least one item
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      detailed_inclusions: prev.detailed_inclusions.length
        ? prev.detailed_inclusions
        : [""],
      exclusions: prev.exclusions.length ? prev.exclusions : [""],
      things_to_carry: prev.things_to_carry.length
        ? prev.things_to_carry
        : [""],
    }));
  }, []);

  const isSelected = (id) => formData.inclusion_highlights.includes(id);

  return (
    <>
      <div className="p-0">
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200/80 bg-slate-50/50 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-bold text-gray-900">
                  Inclusion Highlights
                </h2>
                <p className="mt-0.5 text-xs text-gray-500">
                  Tap to toggle what&apos;s included in this package
                </p>
              </div>
            </div>
            {hasMasterHighlights ? (
              <div
                className={`mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 ${
                  highlightError ? "rounded-lg border border-red-300 p-3" : ""
                }`}
              >
                {masterHighlights.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => handleHighlightToggle(item.id)}
                    className={`group relative flex cursor-pointer items-center gap-2.5 rounded-xl border px-3 py-2.5 transition-all duration-200 ${
                      isSelected(item.id)
                        ? "border-primary bg-primary/5 shadow-sm ring-1 ring-primary/20"
                        : highlightError
                        ? "border-red-200 bg-white hover:border-red-300"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                    }`}
                  >
                    <div className="shrink-0 rounded-full flex items-center justify-center">
                      <InclusionHighlightIcon item={item} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium leading-snug">
                        {item.name}
                      </span>
                    </div>
                    {isSelected(item.id) && (
                      <div className="shrink-0">
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary">
                          <i className="fi fi-rr-check text-[9px] text-white"></i>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4 rounded-lg border border-dashed border-gray-300 bg-white px-4 py-6 text-center">
                <p className="text-sm text-gray-600">
                  No inclusion highlights yet. Add them from General →
                  Inclusions.
                </p>
              </div>
            )}
            {highlightError && (
              <div className="mt-2 text-sm text-red-500">{highlightError}</div>
            )}
          </div>

          <div className="rounded-xl border border-gray-200/80 bg-white p-4">
            <h2 className="text-sm font-bold text-gray-900">
              Detailed Inclusions
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              List specific items included in the package
            </p>
            <div className="mt-4">
            {(formData.detailed_inclusions.length
              ? formData.detailed_inclusions
              : [""]
            ).map((inclusion, index) => (
              <SortableItem
                key={index}
                item={inclusion}
                index={index}
                field="detailed_inclusions"
                placeholder="Enter inclusion detail"
                moveItem={moveItem}
                handleArrayItemChange={handleArrayItemChange}
                removeArrayItem={removeArrayItem}
              />
            ))}
            <button
              onClick={() => addArrayItem("detailed_inclusions")}
              className={pbAddBtnMt}
            >
              Add inclusion
              <i className="fi fi-rr-plus text-xs"></i>
            </button>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200/80 bg-white p-4">
            <h2 className="text-sm font-bold text-gray-900">Exclusions</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              What is not covered in this package
            </p>
            <div className="mt-4">
            {(formData.exclusions.length ? formData.exclusions : [""]).map(
              (exclusion, index) => (
                <SortableItem
                  key={index}
                  item={exclusion}
                  index={index}
                  field="exclusions"
                  placeholder="Enter exclusion detail"
                  moveItem={moveItem}
                  handleArrayItemChange={handleArrayItemChange}
                  removeArrayItem={removeArrayItem}
                />
              )
            )}
            <button
              onClick={() => addArrayItem("exclusions")}
              className={pbAddBtnMt}
            >
              Add exclusion
              <i className="fi fi-rr-plus text-xs"></i>
            </button>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200/80 bg-white p-4">
            <h2 className="text-sm font-bold text-gray-900">
              Things to Carry
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Essential items travellers should bring
            </p>
            <div className="mt-4">
            {(formData.things_to_carry.length
              ? formData.things_to_carry
              : [""]
            ).map((item, index) => (
              <SortableItem
                key={index}
                item={item}
                index={index}
                field="things_to_carry"
                placeholder="Enter required item"
                moveItem={moveItem}
                handleArrayItemChange={handleArrayItemChange}
                removeArrayItem={removeArrayItem}
              />
            ))}
            <button
              onClick={() => addArrayItem("things_to_carry")}
              className={pbAddBtnMt}
            >
              Add item
              <i className="fi fi-rr-plus text-xs"></i>
            </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Inclusion;
